import React, { useState } from 'react';
import { gql } from '@apollo/client';

import { useMutation, useQuery } from 'hooks/apollo';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import { Button, Card, Form, Input, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import useAuth from 'hooks/useAuth';
import Hero from 'components/Hero';
import Loading from 'components/Loading';
import useGlobalContext from 'hooks/useGlobalContext';
import messages from './messages';

const USER_QUERY = gql`
  query PasswordReset($id: ID!, $token: String!) {
    passwordReset(id: $id, token: $token) {
      id
      email      
    }
  }
`;

const SET_PASSWORD_MUTATION = gql`
  mutation UpdatePassword($id: ID!, $token: String!, $password: String!) {
    updatePassword(id: $id, token: $token, password: $password) {
      id
    }
  }
`;

const ResetPassword = () => {
  const { params: { userId, token } } = useRouteMatch();
  const { formatMessage: $t } = useIntl();
  const { message } = useGlobalContext();
  const { login } = useAuth();
  const history = useHistory();

  const { data: { passwordReset } = {}, loading } = useQuery(USER_QUERY, {
    variables: { id: userId, token },
    skip: !userId,
  });

  const [busy, setBusy] = useState(false);
  const [setPassword] = useMutation(SET_PASSWORD_MUTATION);

  const onFinish = async (values) => {
    const { password } = values;
    try {
      setBusy(true);
      await setPassword({
        variables: {
          id: userId,
          token,
          password,
        },
      });
    } catch (error) {
      setBusy(false);
      console.log(error);
      message.error($t(messages[error.message] || messages.updatePasswordFailed));
      return;
    }

    try {
      const loginResult = await login(passwordReset.email, password);
      if (loginResult?.mfa) {
        history.push('/login', {
          email: passwordReset.email,
          mfaPending: true,
        });
      }
    } catch (error) {
      setBusy(false);
      console.log(error);
      history.push('/login', { email: passwordReset.email });
    }
  };

  if (loading) {
    return <Loading />;
  }

  const isValidLink = passwordReset?.email;

  return (
    <Hero branded>
      <Card>
        {!isValidLink && (
          <Result
            status="info"
            title={$t(messages.invalidLinkTitle)}
            subTitle={(
              <>
                <FormattedMessage
                  {...messages.invalidLinkDesc}
                />
                <br />
                <Link to="/forgot-password">
                  {$t(messages.generateLink)}
                </Link>
              </>
            )}
          />
        )}

        {isValidLink && (
          <Form onFinish={onFinish}>
            <p><FormattedMessage {...messages.title} values={{ email: passwordReset.email }} /></p>
            <Form.Item
              name="password"
              rules={[{ required: true, message: $t(messages.passwordRequired) }]}
            >
              <Input.Password
                prefix={<LockOutlined className="input-icon" />}
                placeholder={$t(messages.password)}
              />
            </Form.Item>
            <Form.Item
              name="confirm"
              dependencies={['password']}
              rules={[
                { required: true, message: $t(messages.confirmRequired) },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error($t(messages.passwordMissmatch)));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="input-icon" />}
                placeholder={$t(messages.confirmPassword)}
              />
            </Form.Item>
            <Button block type="primary" htmlType="submit" loading={busy}>
              {$t(messages.submit)}
            </Button>
          </Form>
        )}
      </Card>
    </Hero>
  );
};

export default ResetPassword;
